import {
  Create,
  Datagrid,
  Edit,
  List,
  SimpleForm,
  TextField,
  TextInput,
  TabbedForm,
  FormTab,
  BooleanInput,
  BooleanField,
  ReferenceManyField,
  ChipField,
  FunctionField,
  CreateButton,
  useRecordContext,
  useDataProvider,
  useNotify,
  useRefresh,
  ReferenceInput,
  AutocompleteInput,
  SelectInput,
  DateField,
  useCreatePath,
} from "react-admin";
import { Chip, Typography, Box, Grid, Button, Card, CardContent, Checkbox, FormControlLabel, Alert } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ApiKeyManagement } from "../../components/apikey/ApiKeyManagement";
import { LocationConfigSync } from "../../components/location/LocationConfigSync";

export const RESOURCEID="locationId";

// Location API Keys Section
const LocationApiKeysSection = () => {
  const record = useRecordContext();
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the location first to manage API keys
        </Typography>
      </Box>
    );
  }
  
  return (
    <ApiKeyManagement
      tenantId={String(record.tenantId)}
      brandId={String(record.brandId)}
      locationId={String(record.id)}
      entityType="location"
      entityName={record.name}
    />
  );
};

// Deploy Apps Tab - Deploy applications to devices at this location
const DeployAppsTabContent = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  
  const [selectedApp, setSelectedApp] = useState('');
  const [selectedAppData, setSelectedAppData] = useState<any>(null);
  const [selectedVersion, setSelectedVersion] = useState(1);
  const [deviceTypeCounts, setDeviceTypeCounts] = useState<Record<string, number>>({});
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>([]);
  const [deploying, setDeploying] = useState(false);
  
  // Load device counts by type at this location
  useEffect(() => {
    if (record?.id) {
      loadDeviceCounts();
    }
  }, [record?.id]);
  
  // Load selected application details to get versions
  useEffect(() => {
    if (selectedApp) {
      loadApplicationVersions();
    } else {
      setSelectedAppData(null);
      setSelectedVersion(1);
    }
  }, [selectedApp]);
  
  const loadDeviceCounts = async () => {
    try {
      const { data: devices } = await dataProvider.getList('device', {
        filter: { locationId: record.id },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Count devices by type
      const counts: Record<string, number> = {};
      devices.forEach((device: any) => {
        counts[device.deviceType] = (counts[device.deviceType] || 0) + 1;
      });
      setDeviceTypeCounts(counts);
    } catch (error) {
      console.error('Failed to load device counts:', error);
    }
  };
  
  const loadApplicationVersions = async () => {
    try {
      const { data } = await dataProvider.getOne('application', { id: selectedApp });
      setSelectedAppData(data);
      // Set to current version by default
      setSelectedVersion(data.currentVersion || 1);
    } catch (error) {
      console.error('Failed to load application:', error);
    }
  };
  
  const handleDeviceTypeToggle = (deviceType: string) => {
    setSelectedDeviceTypes(prev => 
      prev.includes(deviceType)
        ? prev.filter(t => t !== deviceType)
        : [...prev, deviceType]
    );
  };
  
  const handleDeploy = async () => {
    if (!selectedApp || selectedDeviceTypes.length === 0) {
      notify('Please select an application and at least one device type', { type: 'warning' });
      return;
    }
    
    setDeploying(true);
    try {
      // Get devices matching selected types
      const { data: devices } = await dataProvider.getList('device', {
        filter: { 
          locationId: record.id,
          deviceType: { $in: selectedDeviceTypes }
        },
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'name', order: 'ASC' }
      });
      
      // Create deployment for each matching device
      // Pass MongoDB _id for foreign keys - pre-save hook will convert to UUID for context
      const deploymentPromises = devices.map((device: any) => {
        console.log('🚀 Creating deployment:', {
          app: selectedApp,
          location: record.id,  // MongoDB _id
          device: device.id,     // MongoDB _id
          locationContext: record.context?.locationId,  // UUID (for reference)
          deviceContext: device.context?.deviceId        // UUID (for reference)
        });
        
        return dataProvider.create('application-deployment', {
          data: {
            applicationId: selectedApp,
            applicationVersion: selectedVersion,
            tenantId: record.tenantId,
            brandId: record.brandId,
            locationId: record.id,    // MongoDB _id (foreign key)
            deviceId: device.id,       // MongoDB _id (foreign key)
            status: 'pending',
            syncStatus: 'local-only',
            deployedBy: 'admin', // TODO: Get actual user
          }
        });
      });
      
      await Promise.all(deploymentPromises);
      
      notify(`Successfully deployed to ${devices.length} devices`, { type: 'success' });
      refresh();
      
      // Reset form
      setSelectedApp('');
      setSelectedDeviceTypes([]);
      
    } catch (error: any) {
      console.error('Deployment failed:', error);
      notify(`Deployment failed: ${error.message}`, { type: 'error' });
    } finally {
      setDeploying(false);
    }
  };
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the location first to deploy applications
        </Typography>
      </Box>
    );
  }
  
  const totalSelectedDevices = selectedDeviceTypes.reduce((sum, type) => sum + (deviceTypeCounts[type] || 0), 0);
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Deploy Applications to Location
      </Typography>
      <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 3 }}>
        Deploy applications to devices at this location by selecting device types
      </Typography>
      
      {/* Deployment Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <ReferenceInput source="deployApp" reference="application" label="Select Application">
                <AutocompleteInput 
                  optionText="name" 
                  fullWidth
                  onChange={(value) => setSelectedApp(value)}
                />
              </ReferenceInput>
            </Grid>
            <Grid item xs={6}>
              {selectedAppData ? (
                <SelectInput
                  source="deployVersion"
                  label="Application Version"
                  choices={selectedAppData.versions?.map((v: any) => ({
                    id: v.versionNumber,
                    name: `v${v.versionNumber} - ${v.description}`
                  })) || [{ id: selectedAppData.currentVersion, name: `v${selectedAppData.currentVersion}` }]}
                  value={selectedVersion}
                  onChange={(e: any) => setSelectedVersion(parseInt(e.target.value))}
                  fullWidth
                />
              ) : (
                <TextInput 
                  source="deployVersion" 
                  label="Application Version" 
                  type="number"
                  value={selectedVersion}
                  onChange={(e: any) => setSelectedVersion(parseInt(e.target.value))}
                  fullWidth
                  disabled
                  helperText="Select an application first"
                />
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Deploy to Device Types:
              </Typography>
              <Grid container spacing={1}>
                {Object.entries(deviceTypeCounts).map(([type, count]) => (
                  <Grid item xs={6} md={4} key={type}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDeviceTypes.includes(type)}
                          onChange={() => handleDeviceTypeToggle(type)}
                        />
                      }
                      label={`${type.toUpperCase()} (${count} device${count !== 1 ? 's' : ''})`}
                    />
                  </Grid>
                ))}
                {Object.keys(deviceTypeCounts).length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="textSecondary">
                      No devices at this location yet. Add devices in the Devices tab first.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Grid>
            
            {selectedDeviceTypes.length > 0 && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Will deploy to <strong>{totalSelectedDevices} device{totalSelectedDevices !== 1 ? 's' : ''}</strong>
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDeploy}
                disabled={!selectedApp || selectedDeviceTypes.length === 0 || deploying}
                startIcon={<AddIcon />}
              >
                {deploying ? 'Deploying...' : `Deploy to ${totalSelectedDevices} Device${totalSelectedDevices !== 1 ? 's' : ''}`}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      
      {/* Current Deployments at Location */}
      <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
        Current Deployments at This Location
      </Typography>
      
      <ReferenceManyField
        label=""
        reference="application-deployment"
        target="locationId"
        sort={{ field: 'deployedAt', order: 'DESC' }}
      >
        <Datagrid bulkActionButtons={false}>
          <FunctionField
            label="Application"
            render={(record: any) => (
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {record.applicationKey || record.applicationId}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Version {record.applicationVersion}
                </Typography>
              </Box>
            )}
          />
          <FunctionField
            label="Target"
            render={(record: any) => {
              if (record.deviceId) return 'Specific Device';
              if (record.targetDeviceTypes?.length > 0) {
                return `${record.targetDeviceTypes.join(', ')}`;
              }
              return 'All Devices';
            }}
          />
          <ChipField source="status" />
          <DateField source="deployedAt" showTime />
          <TextField source="deployedBy" label="By" />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );
};

// Devices Tab Content Component
const DevicesTabContent = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  const createPath = useCreatePath();
  
  if (!record?.id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="textSecondary">
          Save the location first to manage devices
        </Typography>
      </Box>
    );
  }
  
  const handleAddDevice = () => {
    const path = createPath({ resource: 'device', type: 'create' });
    navigate(path, {
      state: {
        record: {
          tenantId: record.tenantId,
          brandId: record.brandId,
          locationId: record.id,
        }
      }
    });
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>
            Devices at this Location
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage devices assigned to this location (POS terminals, printers, kiosks, etc.)
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddDevice}
        >
          Add Device
        </Button>
      </Box>
      
      <ReferenceManyField
        label=""
        reference="device"
        target="locationId"
        sort={{ field: 'name', order: 'ASC' }}
      >
        <Datagrid rowClick="edit">
          <TextField source="name" />
          <ChipField source="deviceType" />
          <ChipField source="status" />
          <TextField source="hardware.manufacturer" label="Manufacturer" />
          <TextField source="hardware.model" label="Model" />
          <TextField source="hardware.serialNumber" label="Serial #" />
          <FunctionField
            label="Health"
            render={(record: any) => {
              if (!record.health?.lastHeartbeat) return <Chip label="No data" size="small" />;
              const lastSeen = new Date(record.health.lastHeartbeat);
              const minutesAgo = Math.floor((Date.now() - lastSeen.getTime()) / 60000);
              const color = minutesAgo < 5 ? 'success' : minutesAgo < 30 ? 'warning' : 'error';
              return <Chip label={`${minutesAgo}m ago`} color={color} size="small" />;
            }}
          />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );
};

export const LocationList = () => (
  <List>
    <Datagrid rowClick="edit">
      <TextField label="Name" source="name" />
      <TextField label="City" source="address.city" />
      <TextField label="State" source="address.state" />
      <BooleanField label="Active" source="isActive" />
      <TextField label="Phone" source="contact.phone" />
    </Datagrid>
  </List>
);

// Helper to check if this is a location-level environment
const isLocationEnvironment = () => {
  return sessionStorage.getItem('hasLocationContext') === 'true';
};

export const LocationEdit = () => {
  const isLocationEnv = isLocationEnvironment();
  
  return (
    <Edit>
      <TabbedForm>
        <FormTab label="Basic Info">
          <TextInput label="Name" source="name" fullWidth required />
          <TextInput label="Description" source="description" fullWidth multiline />
          
          {/* Conditional rendering based on environment type */}
          {isLocationEnv ? (
            <>
              {/* Location environment - Show as read-only text */}
              <Box sx={{ mt: 2, mb: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Tenant and Brand are managed by the parent environment
                </Typography>
              </Box>
            </>
          ) : (
            <>
              {/* Multi-location environment - Show as dropdowns */}
              <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
                <AutocompleteInput optionText="name" fullWidth />
              </ReferenceInput>
              <ReferenceInput source="brandId" reference="brand" label="Brand" required>
                <AutocompleteInput optionText="name" fullWidth />
              </ReferenceInput>
            </>
          )}
          
          <TextInput label="External ID" source="externalId" fullWidth />
          <TextInput label="Timezone" source="timezone" fullWidth />
          <BooleanInput label="Active" source="isActive" />
        </FormTab>
      <FormTab label="Address">
        <TextInput label="Street" source="address.street" fullWidth />
        <TextInput label="City" source="address.city" fullWidth />
        <TextInput label="State" source="address.state" fullWidth />
        <TextInput label="Postal Code" source="address.postalCode" fullWidth />
        <TextInput label="Country" source="address.country" fullWidth />
      </FormTab>
      <FormTab label="Contact">
        <TextInput label="Phone" source="contact.phone" fullWidth />
        <TextInput label="Email" source="contact.email" fullWidth />
        <TextInput label="Website" source="contact.website" fullWidth />
      </FormTab>
      <FormTab label="Devices">
        <DevicesTabContent />
      </FormTab>
      
      <FormTab label="Deploy Apps">
        <DeployAppsTabContent />
      </FormTab>
      
      <FormTab label="API Keys">
        <LocationApiKeysSection />
      </FormTab>
      
      <FormTab label="Sync from Parent">
        <LocationConfigSync />
      </FormTab>
    </TabbedForm>
  </Edit>
  );
};

export const LocationCreate = (props) => (
  <Create {...props}>
    <TabbedForm>
      <FormTab label="Basic Info">
        <TextInput label="Name" source="name" fullWidth required />
        <TextInput label="Description" source="description" fullWidth multiline />
        
        {/* Hierarchy - Tenant and Brand are REQUIRED */}
        <ReferenceInput source="tenantId" reference="tenant" label="Tenant" required>
          <AutocompleteInput optionText="name" fullWidth />
        </ReferenceInput>
        <ReferenceInput source="brandId" reference="brand" label="Brand" required>
          <AutocompleteInput optionText="name" fullWidth />
        </ReferenceInput>
        
        <TextInput label="External ID" source="externalId" fullWidth />
        <TextInput label="Timezone" source="timezone" fullWidth />
        <BooleanInput label="Active" source="isActive" defaultValue={true} />
      </FormTab>
      <FormTab label="Address">
        <TextInput label="Street" source="address.street" fullWidth />
        <TextInput label="City" source="address.city" fullWidth />
        <TextInput label="State" source="address.state" fullWidth />
        <TextInput label="Postal Code" source="address.postalCode" fullWidth />
        <TextInput label="Country" source="address.country" fullWidth />
      </FormTab>
      <FormTab label="Contact">
        <TextInput label="Phone" source="contact.phone" fullWidth />
        <TextInput label="Email" source="contact.email" fullWidth />
        <TextInput label="Website" source="contact.website" fullWidth />
      </FormTab>
    </TabbedForm>
  </Create>
);